import { v4 as uuid } from 'uuid'

import { useCallback, useState } from 'react'

import {
  AdPage,
  AdPlatform,
  AdShape,
  getVanMultiAdPlacements,
  VanPlacementConfigModel,
} from '@marketplace-web/ads/ads-data'
import { CountryCode } from '@marketplace-web/country/country-data'

import { transformVanAdPlacement } from '../useFetchVanPlacement/utils'
import useVanAdLoadtimeLogging from '../useVanAdLoadtimeLogging'
import useRequestedAdLogging from '../useRequestedAdLogging'

type Props = {
  page: AdPage
  placementId: string
  countryCode: CountryCode
  platform: AdPlatform
  shape: AdShape
  consentStatus: boolean
}

const useFetchVanMultiPlacements = ({
  page,
  placementId,
  countryCode,
  platform,
  shape,
  consentStatus,
}: Props) => {
  const [placements, setPlacements] = useState<Array<VanPlacementConfigModel>>([])
  const { onVanAdLoadTimeRequest } = useVanAdLoadtimeLogging({
    countryCode,
    shape,
    page,
    platform,
  })
  const correlationId = uuid()

  const { onRequest: onRequestedAdRequest } = useRequestedAdLogging({
    placementId,
    correlationId,
  })

  const fetchVanMultiAdPlacements = useCallback(
    async (purchaseId?: string | null) => {
      const fetchStartTime = Date.now()
      onRequestedAdRequest()
      const response = await getVanMultiAdPlacements({
        consentStatus,
        page,
        placementId,
        correlationId,
        purchaseId,
      })

      const fetchEndTime = Date.now()
      const fetchTime = fetchEndTime - fetchStartTime

      const config = { page, placementId, fetchTime, platform, shape, countryCode, correlationId }

      if ('errors' in response || !response.ad || response.ad.length === 0) {
        setPlacements([])
        onVanAdLoadTimeRequest(placementId, fetchTime, false)

        return
      }

      const transformedPlacements = response.ad.map(ad => transformVanAdPlacement(config, ad))
      setPlacements(transformedPlacements)

      onVanAdLoadTimeRequest(placementId, fetchTime, true)
    },
    [
      consentStatus,
      page,
      placementId,
      platform,
      shape,
      countryCode,
      onVanAdLoadTimeRequest,
      onRequestedAdRequest,
      correlationId,
    ],
  )

  return {
    placements,
    fetchVanMultiAdPlacements,
  }
}

export default useFetchVanMultiPlacements
